import { useEffect, useState } from 'react';
import {
  Alert,
  Anchor,
  BackgroundImage,
  Box,
  Button,
  Center,
  Divider,
  Group,
  Paper,
  PasswordInput,
  Progress,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconBrandAndroid,
  IconBrandApple,
  IconCheck,
  IconDeviceMobile,
} from '@tabler/icons-react';
import { useAppColorScheme } from '../lib/colorScheme';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../lib/auth/useAuth';

type LinkType = 'recovery' | 'invite';

const IOS_APP_URL = 'https://apps.apple.com/us/app/musical-kultur/id6746753196';
const ANDROID_APP_URL =
  'https://play.google.com/store/apps/details?id=com.dfc.mkapp&pcampaignid=web_share';

function readTypeFromUrl(url: URL): LinkType | null {
  const q = url.searchParams.get('type')?.toLowerCase();
  if (q === 'recovery' || q === 'invite') return q;
  const hash = url.hash?.startsWith('#') ? url.hash.slice(1) : url.hash;
  const h = new URLSearchParams(hash).get('type')?.toLowerCase();
  if (h === 'recovery' || h === 'invite') return h;
  return null;
}

function getPasswordStrength(password: string): {
  score: number;
  label: string;
  color: string;
} {
  if (!password) return { score: 0, label: '', color: 'gray' };
  let score = 0;
  if (password.length >= 8) score += 25;
  if (password.length >= 12) score += 15;
  if (/[A-Z]/.test(password)) score += 20;
  if (/[0-9]/.test(password)) score += 20;
  if (/[^A-Za-z0-9]/.test(password)) score += 20;
  if (score <= 25) return { score, label: 'Schwach', color: 'red' };
  if (score <= 50) return { score, label: 'Mittel', color: 'orange' };
  if (score <= 75) return { score, label: 'Gut', color: 'yellow' };
  return { score, label: 'Stark', color: 'teal' };
}

function AppStoreCard({
  href,
  icon,
  store,
  subtitle,
}: {
  href: string;
  icon: React.ReactNode;
  store: string;
  subtitle: string;
}) {
  return (
    <Anchor
      href={href}
      target="_blank"
      rel="noreferrer"
      underline="never"
      style={{ flex: 1 }}
    >
      <Box
        p="sm"
        style={{
          background: 'var(--app-surface-2, rgba(255,255,255,0.06))',
          border: '1px solid var(--app-border)',
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          transition: 'background 0.15s',
          cursor: 'pointer',
        }}
        className="app-store-card"
      >
        <ThemeIcon variant="light" color="brand" size="lg" radius="md">
          {icon}
        </ThemeIcon>
        <Box>
          <Text size="xs" c="dimmed" lh={1.2}>
            {subtitle}
          </Text>
          <Text size="sm" fw={600} c="var(--color-text)" lh={1.3}>
            {store}
          </Text>
        </Box>
      </Box>
    </Anchor>
  );
}

export function ResetPasswordPage() {
  const nav = useNavigate();
  const { session } = useAuth();
  const { colorScheme } = useAppColorScheme();

  const [ready, setReady] = useState(false);
  const [fatal, setFatal] = useState<string | null>(null);
  const [linkType, setLinkType] = useState<LinkType | null>(null);

  const [p1, setP1] = useState('');
  const [p2, setP2] = useState('');
  const [busy, setBusy] = useState(false);
  const [completed, setCompleted] = useState(false);

  const strength = getPasswordStrength(p1);
  const passwordsMatch = p1 && p2 && p1 === p2;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get('code');
        const tokenHash = url.searchParams.get('token_hash');
        const type = readTypeFromUrl(url);
        if (mounted) setLinkType(type);

        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            const { data } = await supabase.auth.getSession();
            if (!data.session) throw error;
          }
          if (url.searchParams.has('code')) {
            url.searchParams.delete('code');
            window.history.replaceState({}, '', url.toString());
            return;
          }
        } else if (tokenHash && type) {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type,
          });
          if (error) throw error;
        }
      } catch (e: unknown) {
        if (mounted) setFatal((e as { message?: string } | null)?.message ?? String(e));
      } finally {
        if (mounted) setReady(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  async function onSave() {
    if (!p1 || p1.length < 8) {
      notifications.show({
        color: 'yellow',
        title: 'Passwort',
        message: 'Mindestens 8 Zeichen.',
      });
      return;
    }
    if (p1 !== p2) {
      notifications.show({
        color: 'yellow',
        title: 'Passwort',
        message: 'Passwörter stimmen nicht überein.',
      });
      return;
    }

    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: p1 });
      if (error) throw error;
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch {
        // ignore
      }

      notifications.show({
        color: 'teal',
        title: linkType === 'invite' ? 'Willkommen!' : 'Passwort gesetzt',
        message:
          linkType === 'invite'
            ? 'Dein Konto ist jetzt aktiviert. Lade die App herunter und melde dich an.'
            : 'Dein neues Passwort ist jetzt gültig.',
      });
      setCompleted(true);
    } catch (e: unknown) {
      notifications.show({
        color: 'red',
        title: 'Update fehlgeschlagen',
        message: (e as { message?: string } | null)?.message ?? String(e),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box mih="100vh" pos="relative">
      <BackgroundImage
        src="/brand/mk_stage_bg.jpg"
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.18,
          filter: 'saturate(0.95) contrast(1.05)',
        }}
      />
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(1100px 620px at 18% 6%, rgba(69, 61, 181, 0.55), transparent 60%), radial-gradient(900px 520px at 88% 4%, rgba(233, 80, 149, 0.22), transparent 55%), linear-gradient(180deg, rgba(43, 38, 34, 0.85), rgba(49, 44, 40, 0.96))',
        }}
      />
      <Center mih="100vh" p="md">
        <Paper
          radius="lg"
          p={{ base: 'lg', sm: 'xl' }}
          w={{ base: '100%', sm: 460 }}
          maw={460}
          shadow="2xl"
          style={{
            backdropFilter: 'blur(12px)',
            background: 'var(--app-surface)',
            border: '1px solid var(--app-border)',
          }}
          className="app-fade-in"
        >
          <Stack gap="lg">
            {/* ── Logo ── */}
            <Box ta="center">
              <img
                src={colorScheme === 'dark' ? '/brand/mk_logo_white.png' : '/brand/mk_logo_black.png'}
                alt="Musical!Kultur Daaden e.V."
                style={{ height: 38, width: 'auto', opacity: 0.95 }}
              />
            </Box>

            {/* ── Loading skeleton ── */}
            {!ready ? (
              <Stack gap="sm">
                <Skeleton height={28} radius="sm" />
                <Skeleton height={14} radius="sm" width="75%" />
                <Skeleton height={14} radius="sm" width="55%" />
                <Divider color="var(--color-border)" />
                <Skeleton height={44} radius="md" />
                <Skeleton height={44} radius="md" />
                <Skeleton height={42} radius="md" />
              </Stack>
            ) : fatal ? (
              /* ── Fatal error state ── */
              <Stack gap="md">
                <Stack gap={4} ta="center">
                  <ThemeIcon color="red" variant="light" size={52} radius="xl" mx="auto">
                    <IconAlertTriangle size={26} />
                  </ThemeIcon>
                  <Title order={3} c="var(--color-text)">
                    Link ungültig
                  </Title>
                  <Text c="dimmed" size="sm">
                    {linkType === 'invite'
                      ? 'Einladungs-Link ist ungültig oder abgelaufen.'
                      : 'Recovery-Link ist ungültig oder abgelaufen.'}
                  </Text>
                </Stack>
                <Alert
                  color="red"
                  variant="light"
                  radius="md"
                  icon={<IconAlertTriangle size={16} />}
                >
                  <Text size="sm">{fatal}</Text>
                </Alert>
                <Button
                  variant="filled"
                  color="brand"
                  fullWidth
                  onClick={() => nav('/login')}
                >
                  Neuen Link anfordern
                </Button>
              </Stack>
            ) : completed ? (
              /* ── Success state ── */
              linkType === 'invite' ? (
                <Stack gap="lg">
                  <Stack gap={4} ta="center">
                    <ThemeIcon color="teal" variant="light" size={52} radius="xl" mx="auto">
                      <IconCheck size={26} />
                    </ThemeIcon>
                    <Title order={3} c="var(--color-text)">
                      Konto aktiviert 🎉
                    </Title>
                    <Text c="dimmed" size="sm">
                      Herzlich willkommen! Dein Konto ist jetzt aktiv. Lade dir
                      die MusicalKultur-App herunter und melde dich dort mit
                      deiner E‑Mail und deinem Passwort an.
                    </Text>
                  </Stack>
                  <Divider
                    label={
                      <Group gap={4}>
                        <IconDeviceMobile size={14} />
                        <Text size="xs">App herunterladen</Text>
                      </Group>
                    }
                    labelPosition="center"
                    color="var(--color-border)"
                  />
                  <Group grow>
                    <AppStoreCard
                      href={IOS_APP_URL}
                      icon={<IconBrandApple size={18} />}
                      subtitle="Laden im"
                      store="App Store"
                    />
                    <AppStoreCard
                      href={ANDROID_APP_URL}
                      icon={<IconBrandAndroid size={18} />}
                      subtitle="Jetzt bei"
                      store="Google Play"
                    />
                  </Group>
                </Stack>
              ) : (
                <Stack gap="sm">
                  <Stack gap={4} ta="center">
                    <ThemeIcon color="teal" variant="light" size={52} radius="xl" mx="auto">
                      <IconCheck size={26} />
                    </ThemeIcon>
                    <Title order={3} c="var(--color-text)">
                      Passwort gesetzt
                    </Title>
                    <Text c="dimmed" size="sm">
                      Dein neues Passwort ist gültig. Du kannst dich jetzt
                      anmelden.
                    </Text>
                  </Stack>
                  <Button
                    variant="filled"
                    color="brand"
                    fullWidth
                    onClick={() => nav('/login')}
                  >
                    Zum Login
                  </Button>
                </Stack>
              )
            ) : !session ? (
              /* ── No session / invalid link ── */
              <Stack gap="md">
                <Stack gap={4} ta="center">
                  <ThemeIcon color="yellow" variant="light" size={52} radius="xl" mx="auto">
                    <IconAlertTriangle size={26} />
                  </ThemeIcon>
                  <Title order={3} c="var(--color-text)">
                    Sitzung abgelaufen
                  </Title>
                  <Text c="dimmed" size="sm">
                    {linkType === 'invite'
                      ? 'Einladungs-Link ist ungültig oder abgelaufen. Bitte den Admin um eine neue Einladung bitten.'
                      : 'Recovery-Link ist ungültig oder abgelaufen. Bitte erneut anfordern.'}
                  </Text>
                </Stack>
                <Button
                  variant="filled"
                  color="brand"
                  fullWidth
                  onClick={() => nav('/login')}
                >
                  Neuen Link anfordern
                </Button>
              </Stack>
            ) : (
              /* ── Password form ── */
              <Stack gap="sm">
                <Box>
                  <Title order={3} c="var(--color-text)">
                    {linkType === 'invite'
                      ? 'Willkommen!'
                      : 'Passwort zurücksetzen'}
                  </Title>
                  <Text c="dimmed" size="sm" mt={2}>
                    {linkType === 'invite'
                      ? 'Setze jetzt dein persönliches Passwort und aktiviere dein Konto.'
                      : 'Wähle ein neues Passwort für dein Konto.'}
                  </Text>
                </Box>

                {linkType === 'invite' && (
                  <>
                    <Divider
                      label={
                        <Group gap={4}>
                          <IconDeviceMobile size={14} />
                          <Text size="xs">App herunterladen</Text>
                        </Group>
                      }
                      labelPosition="center"
                      color="var(--color-border)"
                    />
                    <Group grow>
                      <AppStoreCard
                        href={IOS_APP_URL}
                        icon={<IconBrandApple size={18} />}
                        subtitle="Laden im"
                        store="App Store"
                      />
                      <AppStoreCard
                        href={ANDROID_APP_URL}
                        icon={<IconBrandAndroid size={18} />}
                        subtitle="Jetzt bei"
                        store="Google Play"
                      />
                    </Group>
                    <Divider color="var(--color-border)" />
                  </>
                )}

                <PasswordInput
                  label="Neues Passwort"
                  placeholder="Mindestens 8 Zeichen"
                  value={p1}
                  onChange={(e) => setP1(e.currentTarget.value)}
                  withAsterisk
                  radius="md"
                />
                {p1 && (
                  <Box>
                    <Group justify="space-between" mb={4}>
                      <Text size="xs" c="dimmed">
                        Passwortstärke
                      </Text>
                      <Text size="xs" c={strength.color} fw={600}>
                        {strength.label}
                      </Text>
                    </Group>
                    <Progress
                      value={strength.score}
                      color={strength.color}
                      size="xs"
                      radius="xl"
                      transitionDuration={200}
                    />
                  </Box>
                )}

                <PasswordInput
                  label="Passwort wiederholen"
                  placeholder="Passwort nochmals eingeben"
                  value={p2}
                  onChange={(e) => setP2(e.currentTarget.value)}
                  withAsterisk
                  radius="md"
                  error={
                    p2 && !passwordsMatch
                      ? 'Passwörter stimmen nicht überein'
                      : undefined
                  }
                />

                <Button
                  onClick={onSave}
                  loading={busy}
                  disabled={!p1 || !p2 || !passwordsMatch}
                  fullWidth
                  size="md"
                  radius="md"
                  color="brand"
                  mt={4}
                >
                  {linkType === 'invite'
                    ? 'Passwort setzen & Konto aktivieren'
                    : 'Neues Passwort speichern'}
                </Button>
              </Stack>
            )}

            {/* ── Back to login ── */}
            {!completed && (
              <Button
                variant="subtle"
                color="gray"
                size="sm"
                fullWidth
                onClick={() => nav('/login')}
                disabled={busy}
              >
                Zurück zum Login
              </Button>
            )}
          </Stack>
        </Paper>
      </Center>
    </Box>
  );
}
